<script setup>
import { computed, watch, onMounted, ref } from "vue";
import { useStore } from "vuex";
import Sidenav from "./examples/Sidenav";
import Navbar from "@/examples/Navbars/Navbar.vue";
import AppFooter from "@/examples/Footer.vue";
import GlobalLoader from "@/components/GlobalLoader.vue";

const store = useStore();
const isNavFixed = computed(() => store.state.isNavFixed);
const darkMode = computed(() => store.state.darkMode);
const isAbsolute = computed(() => store.state.isAbsolute);
const showSidenav = computed(() => store.state.showSidenav);
const layout = computed(() => store.state.layout);
const showNavbar = computed(() => store.state.showNavbar);
const showFooter = computed(() => store.state.showFooter);

const navClasses = computed(() => {
  return {
    "position-sticky bg-white left-auto top-2 z-index-sticky":
      isNavFixed.value && !darkMode.value,
    // Argon usa 'bg-default' (que es el verde). Lo neutralizaremos abajo en el CSS.
    "position-sticky bg-default left-auto top-2 z-index-sticky":
      isNavFixed.value && darkMode.value,
    "position-absolute px-4 mx-0 w-100 z-index-2": isAbsolute.value,
    "px-0 mx-4": !isAbsolute.value,
  };
});

// --- LÓGICA SIMPLE DE TEMA ---
const aplicarTema = (esOscuro) => {
  if (esOscuro) {
    document.body.classList.add('tema-oscuro-azul');
  } else {
    document.body.classList.remove('tema-oscuro-azul');
  }
};

watch(darkMode, (nuevoValor) => aplicarTema(nuevoValor));

// --- LÓGICA DE ACTUALIZACIÓN DE PWA ---
const updateExists = ref(false);
const registration = ref(null);

const updateAvailable = (event) => {
  registration.value = event.detail;
  updateExists.value = true;
};

const refreshApp = () => {
  updateExists.value = false;
  if (!registration.value || !registration.value.waiting) return;
  // Le decimos al nuevo Service Worker que tome el control
  registration.value.waiting.postMessage({ type: 'SKIP_WAITING' });
};

onMounted(() => {
  aplicarTema(darkMode.value);
  // Escuchar el evento que configuramos en registerServiceWorker.js
  document.addEventListener('swUpdated', updateAvailable, { once: true });
});
</script>

<template>
  <div
    v-show="layout === 'landing'"
    class="landing-bg h-100 bg-gradient-primary position-fixed w-100"
  ></div>

  <sidenav v-if="showSidenav" />

  <main
    class="main-content position-relative max-height-vh-100 h-100 border-radius-lg"
  >
    <navbar :class="[navClasses]" v-if="showNavbar" />

    <router-view />

    <app-footer v-show="showFooter" />
  </main>

  <GlobalLoader />

  <!-- PWA UPDATE BANNER -->
  <div v-if="updateExists" class="pwa-update-banner shadow-lg animate__animated animate__slideInUp">
    <div class="d-flex align-items-center justify-content-between px-4 py-3">
      <div class="d-flex align-items-center">
        <i class="bi bi-cloud-arrow-down-fill fs-3 text-gold me-3"></i>
        <div>
          <h6 class="mb-0 text-white font-weight-bolder">Actualización Disponible</h6>
          <small class="text-white-50">TAMEP ha descargado una nueva versión.</small>
        </div>
      </div>
      <button @click="refreshApp" class="btn btn-warning btn-sm mb-0 px-4">ACTUALIZAR AHORA</button>
    </div>
  </div>
</template>

<style>
/* Transición suave y general para no lastimar la vista */
body {
  transition: background-color 0.3s ease, color 0.3s ease;
}

/* MODO OSCURO - AZUL MEDIANOCHE (Estilo Corporativo) */
body.tema-oscuro-azul {
  background-color: #121212 !important; /* Fondo Neutral Oscuro para contrastar con banner azul */
  color: #e2e8f0 !important;
}

/* El banner top se divide del fondo */
.bg-tamep-dark {
  background: linear-gradient(135deg, #052957 0%, #001d3d 100%) !important;
}

/* Sidebar claramente distinguible del fondo y del top banner */
.dark-version .sidenav, .dark-mode .sidenav {
  background-color: #1e1e1e !important;
  border-right: 1px solid #333 !important;
}

.dark-version .sidenav .nav-link, .dark-mode .sidenav .nav-link {
  color: #e0e0e0 !important;
}

/* Neutralizamos el fondo verde de Argon y le damos un azul ligeramente más claro para resaltar */
body.tema-oscuro-azul .bg-default {
  background-color: #052652 !important; 
}

/* BANNER DE ACTUALIZACIÓN PWA */
.pwa-update-banner {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--tamep-dark, #001a33);
  border: 2px solid #ffcc00; /* Gold */
  border-radius: 15px;
  z-index: 9999;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.5);
}
.text-gold { color: #ffcc00; }
</style>